import { useEffect, useState, useCallback, useRef } from "react";
import api, { money, formatDate, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { viewDoc, downloadDoc } from "./DocActions";
import {
  Wrench, Pencil, Loader2, Upload, Eye, Download, Trash2, Send,
  MessageSquare, RefreshCw, Paperclip, FileText, Clock, PlusCircle,
  HardHat, Phone, Mail,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const STATUS = ["New", "In Progress", "Waiting on Contractor", "Completed", "Cancelled"];
const statusTone = {
  Completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  New: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  "In Progress": "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  "Waiting on Contractor": "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Cancelled: "bg-muted text-muted-foreground",
};
const prioTone = {
  Urgent: "bg-red-100 text-red-800 dark:bg-red-900/40",
  High: "bg-orange-100 text-orange-800 dark:bg-orange-900/40",
  Medium: "bg-blue-100 text-blue-800 dark:bg-blue-900/40",
  Low: "bg-muted text-muted-foreground",
};
const actIcon = { note: MessageSquare, status: RefreshCw, attachment: Paperclip, created: PlusCircle, contractor_out: Send, contractor_in: MessageSquare, followup_reminder: Clock };

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <div className="text-sm mt-0.5">{children ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

export default function MaintenanceDetail({ itemId, canWrite, onClose, onEdit, onChanged }) {
  const [it, setIt] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [thread, setThread] = useState({ messages: [], contractor: null, awaiting_reply: false });
  const [cc, setCc] = useState({ channels: ["sms"], message: "", include_details: true, appointment: "", access_instructions: "", docIds: [] });
  const [logText, setLogText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/maintenance/${itemId}`).then((r) => setIt(r.data)).catch(() => toast.error("Could not load work order"));
  }, [itemId]);
  const loadThread = useCallback(() => {
    api.get(`/maintenance/${itemId}/contractor/thread`).then((r) => setThread(r.data)).catch(() => {});
  }, [itemId]);
  useEffect(() => { load(); loadThread(); }, [load, loadThread]);

  const toggleCh = (ch) => setCc((s) => ({ ...s, channels: s.channels.includes(ch) ? s.channels.filter((x) => x !== ch) : [...s.channels, ch] }));
  const toggleDoc = (id) => setCc((s) => ({ ...s, docIds: s.docIds.includes(id) ? s.docIds.filter((x) => x !== id) : [...s.docIds, id] }));

  const sendJob = async () => {
    if (cc.channels.length === 0) { toast.error("Select SMS or Email"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/maintenance/${itemId}/contractor/send`, {
        channels: cc.channels, message: cc.message, include_details: cc.include_details,
        appointment: cc.appointment, access_instructions: cc.access_instructions, attachment_doc_ids: cc.docIds,
      });
      const summary = Object.entries(data.results || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast[data.ok ? "success" : "error"](data.ok ? `Job request sent — ${summary}` : `Send failed — ${summary}`);
      setCc((s) => ({ ...s, message: "", appointment: "", access_instructions: "", docIds: [] }));
      loadThread(); load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const logReply = async () => {
    if (!logText.trim()) return;
    try {
      await api.post(`/maintenance/${itemId}/contractor/log-reply`, { body: logText.trim(), channel: "email" });
      setLogText(""); loadThread(); load(); toast.success("Contractor reply logged");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try { const { data } = await api.put(`/maintenance/${itemId}`, { status }); setIt(data); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/maintenance/${itemId}/notes`, { text: note.trim() }); setIt(data); setNote(""); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/maintenance/${itemId}/attachments`, {
          name: file.name, file_type: file.name.split(".").pop().toLowerCase(),
          size_kb: Math.round(file.size / 1024), data: reader.result,
        });
        setIt(data); toast.success("File uploaded"); onChanged && onChanged();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = async (docId) => {
    setBusy(true);
    try { const { data } = await api.delete(`/maintenance/${itemId}/attachments/${docId}`); setIt(data); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const activity = [...(it?.activity || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-xl max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="maintenance-detail">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Wrench className="w-5 h-5 text-primary" /> Work Order</DialogTitle>
          <DialogDescription>Full work request details, attachments, notes and activity history.</DialogDescription>
        </DialogHeader>

        {!it ? (
          <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-5">
            {/* header row */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`rounded-sm font-normal ${statusTone[it.status] || "bg-muted"}`} data-testid="detail-status-badge">{it.status || "—"}</Badge>
              {it.priority && <Badge className={`rounded-sm font-normal ${prioTone[it.priority] || "bg-muted"}`}>{it.priority} priority</Badge>}
              <div className="ml-auto flex gap-2">
                {canWrite && (
                  <div className="w-48" data-testid="detail-status-select">
                    <Select value={it.status || ""} onValueChange={changeStatus} disabled={busy}>
                      <SelectTrigger className="rounded-lg h-9"><SelectValue placeholder="Change status" /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {canWrite && onEdit && (
                  <Button variant="outline" className="rounded-lg h-9" onClick={() => onEdit(it)} data-testid="detail-edit-btn"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
                )}
              </div>
            </div>

            {/* issue */}
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Issue Description</p>
              <p className="text-sm whitespace-pre-line">{it.issue || "—"}</p>
            </div>

            {/* fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Property">{it.property_name}</Field>
              <Field label="Unit">{it.unit}</Field>
              <Field label="Tenant">{it.tenant_name}</Field>
              <Field label="Assigned Vendor / Technician">{it.contractor}</Field>
              <Field label="Scheduled Date">{it.scheduled_date ? formatDate(it.scheduled_date) : null}</Field>
              <Field label="Priority">{it.priority}</Field>
              <Field label="Estimated Cost">{it.estimated_cost != null && it.estimated_cost !== "" ? money(it.estimated_cost) : null}</Field>
              <Field label="Actual Cost">{it.actual_cost != null && it.actual_cost !== "" ? money(it.actual_cost) : null}</Field>
              <Field label="Invoice Number">{it.invoice_number}</Field>
              <Field label="Warranty">{it.warranty}</Field>
            </div>

            {/* contractor communication */}
            <div className="rounded-xl border border-border p-4" data-testid="contractor-comm-section">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold flex items-center gap-2"><HardHat className="w-4 h-4 text-primary" /> Contractor Communication</p>
                {thread.awaiting_reply && <Badge className="rounded-sm font-normal bg-amber-100 text-amber-800 dark:bg-amber-900/40" data-testid="cc-awaiting-badge">Awaiting reply</Badge>}
              </div>
              {!it.contractor_id ? (
                <p className="text-sm text-muted-foreground">Assign a contractor to this work order (via Edit) to send job requests and track replies here.</p>
              ) : (
                <>
                  {thread.contractor && (
                    <div className="text-xs text-muted-foreground mb-3 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="font-medium text-foreground">{thread.contractor.company || thread.contractor.name}</span>
                      {thread.contractor.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {thread.contractor.phone}</span>}
                      {thread.contractor.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {thread.contractor.email}</span>}
                      {thread.contractor.category && <span>{thread.contractor.category}</span>}
                    </div>
                  )}
                  {thread.messages.length > 0 && (
                    <div className="space-y-2 mb-3 max-h-56 overflow-y-auto" data-testid="contractor-thread">
                      {thread.messages.map((mm) => (
                        <div key={mm.id} className={`flex ${mm.direction === "out" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs ${mm.direction === "out" ? (String(mm.status).startsWith("Failed") ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground") : "bg-muted"}`}>
                            {mm.subject && <p className="font-semibold">{mm.subject}</p>}
                            <p className="whitespace-pre-line">{mm.body}</p>
                            <p className="text-[10px] opacity-70 mt-1">{mm.channel}{mm.direction === "out" ? ` · ${mm.sent_by_name || ""}` : " · reply"} · {fmtDateTime(mm.created_at)}{String(mm.status).startsWith("Failed") ? " · not delivered" : ""}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {canWrite && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <button onClick={() => toggleCh("sms")} data-testid="cc-channel-sms" className={`flex-1 rounded-lg border p-1.5 text-xs inline-flex items-center justify-center gap-1.5 ${cc.channels.includes("sms") ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}><Phone className="w-3.5 h-3.5" /> SMS</button>
                        <button onClick={() => toggleCh("email")} data-testid="cc-channel-email" className={`flex-1 rounded-lg border p-1.5 text-xs inline-flex items-center justify-center gap-1.5 ${cc.channels.includes("email") ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}><Mail className="w-3.5 h-3.5" /> Email</button>
                      </div>
                      <textarea value={cc.message} onChange={(e) => setCc((s) => ({ ...s, message: e.target.value }))} rows={2} data-testid="cc-message" placeholder="Message to contractor…" className="w-full rounded-lg border border-border bg-background p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input value={cc.appointment} onChange={(e) => setCc((s) => ({ ...s, appointment: e.target.value }))} data-testid="cc-appointment" placeholder="Preferred appointment (e.g. Fri 6/6, 9–11am)" className="rounded-lg border border-border bg-background p-2 text-xs" />
                        <input value={cc.access_instructions} onChange={(e) => setCc((s) => ({ ...s, access_instructions: e.target.value }))} data-testid="cc-access" placeholder="Access instructions (lockbox, gate code…)" className="rounded-lg border border-border bg-background p-2 text-xs" />
                      </div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={cc.include_details} onChange={(e) => setCc((s) => ({ ...s, include_details: e.target.checked }))} data-testid="cc-include-details" /> Include job details (property, unit, tenant, issue, priority)
                      </label>
                      {(it.attachments || []).length > 0 && (
                        <div className="text-xs">
                          <p className="text-muted-foreground mb-1">Attach documents:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {(it.attachments || []).map((a) => (
                              <button key={a.doc_id} onClick={() => toggleDoc(a.doc_id)} data-testid={`cc-doc-${a.doc_id}`} className={`px-2 py-0.5 rounded border ${cc.docIds.includes(a.doc_id) ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}>{a.name}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <Button className="rounded-lg" onClick={sendJob} disabled={sending} data-testid="cc-send-btn">{sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send Job Request</Button>
                      </div>
                      <div className="flex items-start gap-2 pt-2 border-t border-border">
                        <textarea value={logText} onChange={(e) => setLogText(e.target.value)} rows={1} data-testid="cc-log-input" placeholder="Log an email reply received from the contractor…" className="flex-1 rounded-lg border border-border bg-background p-2 text-xs resize-none" />
                        <Button variant="outline" className="rounded-lg" onClick={logReply} disabled={!logText.trim()} data-testid="cc-log-btn" title="Log contractor email reply"><PlusCircle className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4 text-primary" /> Photos & Documents</p>
                {canWrite && (
                  <>
                    <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="detail-fileinput" />
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={() => fileRef.current?.click()} data-testid="detail-upload-btn"><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload</Button>
                  </>
                )}
              </div>
              {(it.attachments || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No photos or documents uploaded yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(it.attachments || []).map((a) => (
                    <div key={a.doc_id} data-testid={`attachment-${a.doc_id}`} className="flex items-center gap-2 rounded-lg border border-border p-2">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate flex-1">{a.name}</span>
                      <button onClick={() => viewDoc(a.doc_id)} data-testid={`attachment-view-${a.doc_id}`} className="text-primary hover:opacity-70" title="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => downloadDoc(a.doc_id)} className="text-muted-foreground hover:text-primary" title="Download"><Download className="w-4 h-4" /></button>
                      {canWrite && <button onClick={() => removeAttachment(a.doc_id)} data-testid={`attachment-delete-${a.doc_id}`} className="text-muted-foreground hover:text-destructive" title="Remove"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* notes / activity history */}
            <div>
              <p className="text-sm font-semibold flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-primary" /> Notes & Activity History</p>
              {canWrite && (
                <div className="flex items-start gap-2 mb-3">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-note-input"
                    placeholder="Add a note about this work order…" rows={2}
                    className="flex-1 rounded-lg border border-border bg-background p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  <Button className="rounded-lg" onClick={addNote} disabled={busy || !note.trim()} data-testid="detail-add-note-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              )}
              {activity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity yet.</p>
              ) : (
                <div className="space-y-2" data-testid="detail-activity-log">
                  {activity.map((a) => {
                    const Icon = actIcon[a.type] || MessageSquare;
                    return (
                      <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-3 rounded-lg border border-border p-2.5">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${a.type === "note" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm whitespace-pre-line">{a.text}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{a.user_name || a.user || "System"} · {fmtDateTime(a.at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
